/**
 * Field-level validators.
 * Each validator returns `true` when valid, or an error message (string).
 */

export const validators = {
  customerName: (v = "") =>
    v.trim().length >= 2 || "Enter the customer's full name",

  mobileNumber: (v = "") =>
    /^\d{10}$/.test(v) || "Enter a valid 10-digit mobile number",

  email: (v = "") =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || "Enter a valid email address",

  city: (v = "") => (v && v.trim().length >= 2) || "Select a city",

  state: (v = "") => (v && v.trim().length >= 2) || "Select a state",

  pinCode: (v = "") => /^\d{5,6}$/.test(v) || "Enter a valid pin code",

  productCategory: (v = "") =>
    (v && v.trim().length >= 1) || "Select a product category",

  brandName: (v = "") => (v && v.trim().length >= 1) || "Select a brand name",

  serialNumber: (v = "") =>
    v.trim().length >= 4 || "Enter the product serial number",

  invoiceValue: (v = "") =>
    (/^\d+(\.\d{1,2})?$/.test(v.trim()) && parseFloat(v) > 0) ||
    "Enter a valid invoice amount",

  purchaseDate: (v = "") =>
    (v && v.trim().length > 0) || "Select a purchase date",
};

/**
 * Runs a single field's validator.
 * @returns {true|string}
 */
export const runValidator = (fieldId, value) => {
  const rule = validators[fieldId];
  if (!rule) return true;
  return rule(value ?? "");
};

/**
 * Validates an entire values object against `validators`.
 * @returns {{ isValid: boolean, errors: Record<string,string> }}
 */
export const validateAll = (values) => {
  const errors = {};
  let isValid = true;

  Object.keys(validators).forEach((fieldId) => {
    const result = runValidator(fieldId, values[fieldId]);
    if (result !== true) {
      errors[fieldId] = result;
      isValid = false;
    }
  });

  return { isValid, errors };
};

/** Strips everything but digits, then trims to maxLength */
export const sanitizeDigits = (value = "", maxLength) =>
  value.replace(/\D/g, "").slice(0, maxLength);

/** Invoice file constraints */
export const INVOICE_MAX_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB
export const INVOICE_ACCEPTED_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/jpg",
];

/**
 * Validates an uploaded invoice file.
 * @returns {true|string}
 */
export const validateInvoiceFile = (file) => {
  if (!file) return "Please attach a copy of the invoice";
  if (file.size > INVOICE_MAX_SIZE_BYTES) return "File exceeds 10 MB limit";
  if (!INVOICE_ACCEPTED_TYPES.includes(file.type))
    return "Only PDF, JPG or PNG files are supported";
  return true;
};

/**
 * Validates the product video.
 * @returns {true|string}
 */
export const validateProductVideo = (file) => {
  if (!file) return "Product video is mandatory — record or upload one";
  return true;
};
